import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Conversation, ConversationsState, formatConversationDate } from '../../../../lib/conversations';
import { useConversationSearch, WebSearchResult } from '../../../../lib/search';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useTranslation } from '../../../../lib/i18n';
import { Button, IconButton, ScrollArea } from '../../ui';
import { useConversationSwitch } from '../hooks/use-conversation-switch';
import { useConversationActions, CONVERSATIONS_PAGE, type ConversationActionsFlow } from '../../../../lib/use-conversation-actions';
import { ConversationRowMenu } from '../../conversations/ConversationRowMenu';
import { DeleteConversationConfirm } from '../../conversations/DeleteConversationConfirm';
import { RenameConversationDialog } from '../../conversations/RenameConversationDialog';
import rowStyles from './ConversationRow.module.css';
import { useMenuSearchBi } from '../hooks/use-menu-search-bi';
import { useKeyboardInset } from '../../use-keyboard-inset';
import { RailCatcher } from '../../../RailCatcher';
import styles from './SearchDialog.module.css';

// The Symphony search dialog — a centered command palette opened from the header
// search icon. Empty query shows recent chats; typing shows conversation search
// results. Picking a chat switches it IN PLACE (the side chat) and closes the
// dialog — no route navigation, so the screen never reloads. Escape / clicking
// the scrim also closes. The scrim is scoped to the content pane so the rail
// stays crisp (matches the Figma + the shared Modal shell) — and the rail gets a
// RailCatcher so "crisp" doesn't also mean "still clickable underneath".
export function SearchDialog({
  state,
  selectedId,
  onClose,
}: {
  state: ConversationsState;
  selectedId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  // On mobile, iOS Safari doesn't shrink the layout viewport for the keyboard —
  // only visualViewport does — so a centered/full-height dialog re-centers and
  // visibly jumps as the keyboard opens under the auto-focused input below.
  // Track the gap and subtract it from the card's height instead.
  const keyboardInset = useKeyboardInset();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const trimmed = query.trim();

  return createPortal(
    <div
      className={styles.scrim}
      style={{ ['--kb-inset' as string]: `${keyboardInset}px` }}
      // A press that both starts AND ends on the scrim closes — a text selection
      // that drifts out of the card won't dismiss it.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <RailCatcher onClose={onClose} zIndex={1000} />
      <div role="dialog" aria-modal="true" aria-label={t('sidebar.search.placeholderSymphony')} className={styles.card}>
        <div className={styles.header}>
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('search.dialog.placeholder')}
          />
          <div className={styles.controls}>
            <Button variant="text" size="tiny" onClick={() => setQuery('')}>
              {t('search.dialog.clear')}
            </Button>
            <span className={styles.vdivider} aria-hidden />
            <IconButton size="tiny" aria-label={t('common.close')} onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </IconButton>
          </div>
        </div>
        <div className={styles.divider} aria-hidden />
        <ScrollArea className={styles.body}>
          {trimmed ? (
            <Results query={query} state={state} selectedId={selectedId} onClose={onClose} />
          ) : (
            <RecentChats state={state} selectedId={selectedId} onClose={onClose} />
          )}
        </ScrollArea>
      </div>
    </div>,
    document.body,
  );
}

function RecentChats({
  state,
  selectedId,
  onClose,
}: {
  state: ConversationsState;
  selectedId: string | null;
  onClose: () => void;
}) {
  const recent = state.kind === 'ready' ? state.conversations.slice(0, 10) : [];
  const flow = useConversationActions(CONVERSATIONS_PAGE);
  if (recent.length === 0) return null;
  return (
    <>
      {recent.map((c) => (
        <RecentRow key={c.id} conversation={c} selected={c.id === selectedId} onClose={onClose} flow={flow} />
      ))}
      <DeleteConversationConfirm flow={flow} />
      <RenameConversationDialog flow={flow} />
    </>
  );
}

function RecentRow({
  conversation,
  selected,
  onClose,
  flow,
}: {
  conversation: Conversation;
  selected: boolean;
  onClose: () => void;
  flow: ConversationActionsFlow;
}) {
  const { logPageClickBi } = useGenericBi();
  const { t } = useTranslation();
  const switchConversation = useConversationSwitch();
  return (
    <ChatRow
      selected={selected}
      onPick={(e) => {
        if (conversation.id) {
          logPageClickBi({ page_name: 'menu', button_name: 'chat_history', origin: 'menu', conversation_id: conversation.id });
        }
        switchConversation(conversation.id, e);
        onClose();
      }}
      title={conversation.name || t('common.untitled')}
      date={formatConversationDate(conversation.updatedDate || conversation.createdDate)}
      menu={
        <ConversationRowMenu
          conv={conversation}
          title={conversation.name || t('common.untitled')}
          flow={flow}
        />
      }
    />
  );
}

function Results({
  query,
  state,
  selectedId,
  onClose,
}: {
  query: string;
  state: ConversationsState;
  selectedId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const search = useConversationSearch(query);
  useMenuSearchBi(query, search);
  if (search.kind === 'idle') return null;
  if (search.kind === 'loading') {
    return (
      <>
        {[68, 84, 56].map((w, i) => (
          <div key={i} className={styles.shimmerRow}>
            <div className="orion-shimmer" style={{ height: 14, width: `${w}%`, borderRadius: 6 }} />
          </div>
        ))}
      </>
    );
  }
  if (search.kind === 'error') return <div className={styles.error}>{search.message}</div>;
  if (search.results.length === 0) {
    return <div className={styles.empty}>{t('sidebar.search.noMatches', { query: search.query })}</div>;
  }
  // A result carries its own createdDate; when it doesn't (e.g. an older API), fall
  // back to the matching loaded conversation's date so every row shows a date/time.
  const convById = new Map(
    (state.kind === 'ready' ? state.conversations : []).map((c) => [c.id, c] as const),
  );
  return (
    <>
      {search.results.map((r, i) => {
        const conv = r.entityId ? convById.get(r.entityId) : undefined;
        const iso = r.createdDate || conv?.updatedDate || conv?.createdDate;
        return (
          <ResultRow
            key={`${r.entityId ?? 'r'}-${i}`}
            result={r}
            query={search.query}
            date={iso ? formatConversationDate(iso) : undefined}
            selected={r.entityId === selectedId}
            onClose={onClose}
          />
        );
      })}
    </>
  );
}

function ResultRow({
  result,
  query,
  date,
  selected,
  onClose,
}: {
  result: WebSearchResult;
  query: string;
  date?: string;
  selected: boolean;
  onClose: () => void;
}) {
  const { logPageClickBi } = useGenericBi();
  const { t } = useTranslation();
  const switchConversation = useConversationSwitch();
  return (
    <ChatRow
      selected={selected}
      onPick={(e) => {
        if (result.entityId) {
          logPageClickBi({ page_name: 'menu', button_name: 'chat_history', origin: 'menu', conversation_id: result.entityId });
        }
        switchConversation(result.entityId, e);
        onClose();
      }}
      title={<Highlighted text={result.title || t('common.untitled')} query={query} />}
      subtitle={result.subtitle ? <Highlighted text={result.subtitle} query={query} /> : undefined}
      date={date}
    />
  );
}

// A row is a button, not a link — picking switches the conversation in place (the
// side chat) via the store, so the screen never navigates/reloads.
function ChatRow({
  selected,
  onPick,
  title,
  subtitle,
  date,
  menu,
}: {
  selected: boolean;
  onPick: (e: React.MouseEvent) => void;
  title: ReactNode;
  subtitle?: ReactNode;
  date?: string;
  /** The row's 3-dot menu. A SIBLING of the button, never a child: a button inside a button is invalid
   *  markup, and the nested one would never receive its own clicks. */
  menu?: ReactNode;
}) {
  const row = (
    <button type="button" className={`${styles.row} ${selected ? styles.rowSelected : ''} ${menu ? rowStyles.link : ''}`} onClick={onPick}>
      <span className={styles.rowText}>
        <span className={styles.rowTitle}>{title}</span>
        {subtitle && <span className={styles.rowSubtitle}>{subtitle}</span>}
      </span>
      {date && <span className={styles.rowDate}>{date}</span>}
    </button>
  );
  if (!menu) return row;
  return (
    <div className={rowStyles.row}>
      {row}
      <span className={rowStyles.menu}>{menu}</span>
    </div>
  );
}

// Server returns plain snippets without offsets, so highlight on the client —
// case-insensitive whole-substring match.
function Highlighted({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const parts: ReactNode[] = [];
  let cursor = 0;
  while (cursor < text.length) {
    const idx = lower.indexOf(q, cursor);
    if (idx === -1) {
      parts.push(text.slice(cursor));
      break;
    }
    if (idx > cursor) parts.push(text.slice(cursor, idx));
    parts.push(
      <mark key={`m-${idx}`} className={styles.mark}>
        {text.slice(idx, idx + q.length)}
      </mark>,
    );
    cursor = idx + q.length;
  }
  return <>{parts}</>;
}
